/**
 * @file        job/upload-slices.js
 * @author      Ryan Saweczko, ryansaweczko@kingsds.network
 *              Danny Akbarzadeh, danny@kingsds.network
 * 
 * @date        Jun 2022
 *
 * Implement functions to upload slices to the scheduler after a job has been deployed.
 * This area will have it's own connection to the job submit service which it is responsible
 * for handling.
 */

const protocol = require('dcp/protocol');

const debugging = require('dcp/debugging').scope('dcp-client');

let uploadConnection = null;
let uploadRefs = 0;
let tunedKvin;

function createNewConnection()
{
  uploadConnection = new protocol.Connection(dcpConfig.scheduler.services.jobSubmit, { allowBatch: false });
  uploadConnection.on('end', createNewConnection);
  return uploadConnection.connect();
}

/**
 * Helper function that tries to upload slicePile to scheduler for the job with the given address
 * If the connection throws, we will continue trying to upload until it has thrown errorTolerance times
 * However, if the upload is unsuccessful, we throw immediately.
 * @param {object} pileMessage 
 * @returns payload containing success property (pertaining to success of adding slices to job) as well as lastSliceNumber of job 
 */
async function safeSliceUpload(pileMessage)
{
  let payload = undefined;
  let errorTolerance = dcpConfig.job.sliceUploadErrorTolerance;           // copy number of times we will tolerate non-success when uploading slices directly from config
  let thisConnection = uploadConnection                                   // memoize the connection used to upload slices, to only close this one if send fails

  while (true)                                                            // eslint-disable-line no-constant-condition
  {
    try
    {
      const start = Date.now();
      debugging('slice-upload') && console.log('x-dbg-uploadStart', pileMessage.payload.jsonData.length);

      payload = await pileMessage.send();
      if (!payload.success)
      {
        debugging('slice-upload') && console.log('x-dbg-uploadBackoff', {length:pileMessage.payload.jsonData.length});
        throw payload;
      }
      else
      {
        debugging('slice-upload') && console.log('x-dbg-uploadProgress', Date.now() - start);
        break;
      }
    }
    catch (error)
    {
      if (--errorTolerance <= 0)
      {
        debugging('slice-upload') && console.log('x-dbg-uploadError', error);
        if (uploadConnection === thisConnection)
        {
          uploadConnection.off('end', createNewConnection);
          uploadConnection.close(); // ensure we clean up the connection
        }
        throw error;
      }
    }
  }

  return payload;
}

/**
* This function contains the actual logic behind staggered slice uploads
* to the scheduler which makes quicker deployment possible.
* 
* Note that we pass in mostToTake so that the uploadLogic function can update 
* it to the new value it needs to be, and then pass it back to the wrapper 
* function (addSlices) which actually does the work of picking up slices 
* and thus uses this value
* @param {Array} pile                the actual array of slices being uploaded to scheduler
* @param {Number} mostToTake         number of slices that should be taken by the wrapper function (addSlices) 
*                                    which actually does the work of picking up slices and thus uses this value.
*                                    We pass in mostToTake so that the uploadLogic function can update it to the 
*                                    new value it needs to be, and then pass it back to the wrapper
* @param {*} jobId              Address of job to upload the slices to  
* @returns payload containing success property (pertaining to success of adding slices to job) as well as lastSliceNumber of job
*/
async function sliceUploadLogic(pile, mostToTake, jobId)
{
  if (!uploadConnection)
    createNewConnection();

  const slicesTaken = pile.length;
  const payloadData = {
    job: jobId,
    dataValues: getKvin().marshal(pile)
  };
  const pileMessage = new uploadConnection.Request({
    /* payload object */
    operation: 'addSliceData',
    jsonData:  JSON.stringify(payloadData) /* becomes payload.data in transit */
  });
  const pileSize = pileMessage.payload.jsonData.length;
  
  let newMostToTake;
  let uploadedSlices;

  // if the pile is larger than the ceiling but we only took one slice, there's no smaller pile we can make
  //    so we upload it anyway but we don't try taking more next time cause we were over the ceiling (which 
  //    is a hard limit on upload sizes)
  if ((pileSize > dcpConfig.job.uploadSlicesCeiling) && (slicesTaken === 1))
  {
    uploadedSlices = await safeSliceUpload(pileMessage);
    newMostToTake = 1;
  }
      
  // if the pile is larger than the target but we only took one slice, there's no smaller pile we can make
  //    so we upload it anyway and still try taking more
  else if ((pileSize > dcpConfig.job.uploadSlicesTarget) && (slicesTaken === 1))
  {
    uploadedSlices = await safeSliceUpload(pileMessage);
    newMostToTake = mostToTake * dcpConfig.job.uploadIncreaseFactor;
  }
  
  // otherwise, if the pile is smaller than the soft ceiling, send up the pile anyway (since piles are expensive to make) 
  //    but remember to include incrementFactor times as many slices in the next pile
  else if (pileSize <= dcpConfig.job.uploadSlicesTarget)
  {
    uploadedSlices = await safeSliceUpload(pileMessage);
    newMostToTake = mostToTake * dcpConfig.job.uploadIncreaseFactor;
  }
  
  // if the pile is over the ceiling then we do not upload and begin reassembling our piles from scratch
  else if (pileSize > dcpConfig.job.uploadSlicesCeiling)
  {
    newMostToTake = -1;
  }
  
  // if the pile is over the target (but implicitly under the ceiling), then upload the pile to scheduler but lower mostToTake
  //    by a smaller factor than incrementFactor to allow us to begin "centering" sizes of piles around the target
  else if (pileSize > dcpConfig.job.uploadSlicesTarget)
  {
    uploadedSlices = await safeSliceUpload(pileMessage);
    newMostToTake = Math.ceil(mostToTake / ((2 / 3) * dcpConfig.job.uploadIncreaseFactor));
  }
  else
    throw new Error('hopefully impossible code in slice upload logic');

  let payload = uploadedSlices ? uploadedSlices.payload : undefined;
  return { payload, newMostToTake }; // in case the user needs lastSliceNumber's value
}

/**
* Uploads slices to the scheduler in a staggered fashion
*  @param {Array}  dataValues       actual array of slices being uploaded to scheduler
*  @param {*} jobId            Address of job these slices are for
*  @param {*} newTunedKvin          undefined, or new version of kvin tuned for speed or size specifically. Use if defined.
*  @returns                         payload containing success property (pertaining to success of adding slices to job) as well as lastSliceNumber of job
*/
async function addSlices(dataValues, jobId, newTunedKvin)
{
  if (dataValues.length === 0)
    return;

  if (newTunedKvin)
    tunedKvin = newTunedKvin;

  if (!Array.isArray(dataValues))
    throw new TypeError('Only data-by-value jobs may dynamically add slices');

  let mostToTake = dcpConfig.job.uploadInitialNumberOfSlices;            // maximum number of slices we could take in per pile
  let payload = undefined;                                               // used in return value
  let slicesTaken = 0;                                                   // number of slices in the pile already
  let pile = [];
  uploadRefs++;

  for (let slice of dataValues)
  {
    pile.push(slice);
    slicesTaken++;
    if (slicesTaken === mostToTake)
    {
      let total = await sliceUploadLogic(pile, mostToTake, jobId);
      payload = total.payload;
      
      if (total.newMostToTake < 0)
      {
      /* if total.newMostToTake == -1 (only non-positive value returned), then the pile was not successfully
      *  uploaded because it was over the ceiling and we need to upload the pile *itself* again, recursively
      */
        payload = await addSlices(pile, jobId);
        /* and next time, the number of slices we take is the number from this time *divided* by the incrementFactor
        *    since we know invariably that number of slices was under the ceiling AND target
        *  if you're curious why that's an invariant, this is because mostToTake only ever *increases* by being multiplied by 
        *    a factor of incrementFactor within sliceUploadLogic, and this only occurs when the pile being uploaded that time
        *    was under the target
        */
        mostToTake = mostToTake / dcpConfig.job.uploadIncreaseFactor;
      }
      else
      {
        /* in all other cases (other than the pile size being over the ceiling) the sliceUploadLogic helper 
        *  determines the number of slices we should pick up next time, so we just use the value it spits out
        */
        mostToTake = total.newMostToTake;
      }
      
      // reset slicesTaken and pile since at this point we know for sure the pile has been uploaded
      pile = [];
      slicesTaken = 0;
    }
  }
  // upload the pile one last time in case we continued off the last slice with a non-empty pile
  if (pile.length !== 0)
  {
    let finalObj = await sliceUploadLogic(pile, mostToTake, jobId);
    payload = finalObj.payload;
    mostToTake = finalObj.newMostToTake;
      
    if (mostToTake < 0)
    {
      // if you need documentation on the next two lines, look inside the if (total.newMostToTake < 0) just above
      payload = await addSlices(pile, jobId);
      mostToTake = mostToTake / dcpConfig.job.uploadIncreaseFactor;
    }
  }

  // and finally assign whatever mostToTake was at the end of this run of the function to be returned 
  //    as part of the payload in case addSlices was called recursively
  payload.mostToTake = mostToTake;

  // cleanup connection when done with this function
  uploadRefs--;
  if (uploadConnection && uploadRefs === 0) {
    debugging('slice-upload') && console.debug(`250: closing uploadConnection`, uploadConnection?.id);
    uploadConnection.removeAllListeners('end');
    uploadConnection.close();
    uploadConnection = null;
    debugging('slice-upload') && console.debug(`252: closed uploadConnection`, uploadConnection);
  }

  /* contains the job's lastSliceNumber (the only externally-meaningful value returned from 
  *  the uploading of slices to the scheduler) in case the calling function needs it 
  */
  return payload;
}

/**
 * marshal the value using kvin or instance of the kvin (tunedKvin)
 * tunedKvin is defined if job.tuning.kvin is specified.
 *
 * @param  {any}    value 
 * @return {object} A marshaled object
 * 
 */
function getKvin()
{
  return tunedKvin || require('kvin');
}


exports.addSlices = addSlices;
